import { useState } from 'react';
import { useUpdateProjectSettings } from '../api/projects';

/**
 * Inline editor for the project's `default_branch` — the base used in
 * GitHub compare URLs. Shown next to the branch link as a small
 * "vs `<base>`" pill so users can fix it when their repo's actual
 * default branch differs from the panel's stored value (e.g. `master`
 * vs `main`).
 *
 * Click → text input appears; Enter saves, Escape cancels.
 */
export default function BaseBranchPrompt({
  projectSlug,
  currentBase,
}: {
  projectSlug: string;
  currentBase: string;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentBase);
  const [err, setErr] = useState<string | null>(null);
  const mutation = useUpdateProjectSettings(projectSlug);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setValue(currentBase);
          setErr(null);
          setOpen(true);
        }}
        className="text-[10px] text-slate-500 hover:text-sky-700 hover:underline decoration-dotted"
        title="Change the base branch used in the compare URL (e.g. main, master, develop)"
      >
        vs <span className="font-mono">{currentBase}</span>
      </button>
    );
  }

  async function save(): Promise<void> {
    setErr(null);
    const trimmed = value.trim();
    if (!trimmed) {
      setErr('Branch name required.');
      return;
    }
    if (trimmed === currentBase) {
      setOpen(false);
      return;
    }
    try {
      await mutation.mutateAsync({ default_branch: trimmed });
      setOpen(false);
    } catch (e) {
      setErr((e as Error).message || 'Failed to save.');
    }
  }

  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-[10px] text-slate-500">vs</span>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="main"
        className="text-[11px] font-mono border border-slate-300 rounded px-1.5 py-0.5 w-28"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') {
            setValue(currentBase);
            setOpen(false);
            setErr(null);
          }
        }}
      />
      <button
        type="button"
        onClick={() => void save()}
        disabled={mutation.isPending}
        className="text-[11px] px-1.5 py-0.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
      >
        {mutation.isPending ? '…' : 'save'}
      </button>
      <button
        type="button"
        onClick={() => {
          setValue(currentBase);
          setOpen(false);
          setErr(null);
        }}
        className="text-[11px] text-slate-500 hover:text-slate-700"
      >
        cancel
      </button>
      {err && <span className="text-[10px] text-red-600">{err}</span>}
    </span>
  );
}
